<script setup>
import { ref, onMounted, onBeforeUnmount, computed, h, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer } from 'ant-design-vue';
import { 
  MenuFoldOutlined, 
  MenuUnfoldOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  PlusOutlined,
  TagsOutlined,
  LogoutOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons-vue';
import { Tooltip } from 'ant-design-vue';

const { Header, Content, Sider } = Layout;

const router = useRouter();
const authStore = useAuthStore();
const collapsed = ref(false);
const selectedKeys = ref(['1']);
const drawerVisible = ref(false);
const isMobile = ref(window.innerWidth < 768);

// 监听窗口大小变化
const handleResize = () => {
  isMobile.value = window.innerWidth < 768;
  if (!isMobile.value) {
    drawerVisible.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  updateSelectedMenu();
  router.afterEach(() => {
    updateSelectedMenu();
    if (isMobile.value) {
      drawerVisible.value = false;
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// 切换侧边栏
const toggleCollapsed = () => {
  if (isMobile.value) {
    drawerVisible.value = !drawerVisible.value;
  } else {
    collapsed.value = !collapsed.value;
  }
};

const menuItems = [
  { 
    key: '1', 
    icon: () => h(DashboardOutlined),
    label: '控制面板',
    path: '/'
  },
  { 
    key: '2', 
    icon: () => h(AppstoreOutlined),
    label: '我的订阅',
    path: '/subscriptions'
  },
  { 
    key: '3', 
    icon: () => h(PlusOutlined),
    label: '添加订阅',
    path: '/subscriptions/add'
  },
  { 
    key: '4', 
    icon: () => h(TagsOutlined),
    label: '分类管理',
    path: '/categories'
  },
  { 
    key: '5', 
    icon: () => h(QuestionCircleOutlined),
    label: '技术支持',
    path: '/support'
  }
];

// 移动端底部导航栏菜单项
const mobileMenuItems = [
  { key: '1', icon: DashboardOutlined, label: '首页', path: '/' },
  { key: '2', icon: AppstoreOutlined, label: '订阅', path: '/subscriptions' },
  { key: '3', icon: PlusOutlined, label: '添加', path: '/subscriptions/add' },
  { key: '4', icon: TagsOutlined, label: '分类', path: '/categories' },
  { key: '5', icon: QuestionCircleOutlined, label: '帮助', path: '/support' },
];

const handleMenuClick = ({ key }) => {
  const item = menuItems.find(item => item.key === key);
  if (item) {
    router.push(item.path);
  }
};

// 移动端标签点击事件
const handleMobileTabClick = (item) => {
  selectedKeys.value = [item.key];
  router.push(item.path);
};

const handleLogout = async () => {
  await authStore.logout();
  router.push('/login');
};

const userMenuItems = [
  {
    key: 'support',
    label: '技术支持',
    icon: () => h(CustomerServiceOutlined)
  },
  {
    type: 'divider',
  },
  {
    key: 'logout',
    label: '退出登录',
    icon: markRaw(LogoutOutlined),
    danger: true
  }
];

// 处理用户菜单点击
const handleUserMenuClick = ({ key }) => {
  if (key === 'logout') {
    handleLogout();
  }
};

// Update selected menu item based on current route
const updateSelectedMenu = () => {
  const currentPath = router.currentRoute.value.path;
  const currentItem = menuItems.find(item => item.path === currentPath);
  if (currentItem) {
    selectedKeys.value = [currentItem.key];
  }
};

onMounted(() => {
  updateSelectedMenu();
  router.afterEach(updateSelectedMenu);
});
</script>

<template>
  <a-layout style="min-height: 100vh">
    <!-- 移动端底部导航栏 -->
    <template v-if="isMobile">
      <div class="mobile-tab-bar">
        <template v-for="item in mobileMenuItems" :key="item.key">
          <div 
            class="tab-item"
            :class="{ 'active': selectedKeys[0] === item.key }"
            @click="handleMobileTabClick(item)"
          >
            <component :is="item.icon" class="tab-icon" />
            <span class="tab-label">{{ item.label }}</span>
          </div>
        </template>
      </div>
    </template>

    <!-- 桌面端侧边栏 -->
    <a-layout-sider 
      v-if="!isMobile"
      v-model:collapsed="collapsed" 
      :trigger="null" 
      collapsible
      :collapsedWidth="80"
      width="200"
    >
      <div class="logo"><img src="@/assets/dingyuebox-title-with-icon-white.png" alt="Logo" /></div>
      <a-menu
        v-model:selectedKeys="selectedKeys"
        theme="dark"
        mode="inline"
        :items="menuItems"
        @click="handleMenuClick"
      />
    </a-layout-sider>

    <a-layout>
      <a-layout-header class="header">
        <div class="header-content">
          <div v-if="!isMobile" class="trigger" @click="toggleCollapsed">
            <menu-unfold-outlined v-if="collapsed" />
            <menu-fold-outlined v-else />
          </div>
          <div v-else class="mobile-header">
            <h1 class="app-title">DingYueBox</h1>
          </div>
          <div class="user-info" style="display: flex; align-items: center; gap: 16px;">
            <span class="username" v-if="!isMobile">
              {{ authStore.user?.username || '用户' }}
            </span>
            <a-tooltip title="退出登录" placement="bottom">
              <a-button 
                type="text" 
                shape="circle" 
                @click="handleLogout"
                style="display: flex; align-items: center; justify-content: center;"
              >
                <template #icon><LogoutOutlined /></template>
              </a-button>
            </a-tooltip>
          </div>
        </div>
      </a-layout-header>
      <a-layout-content class="content">
        <div class="content-wrapper">
          <router-view />
        </div>
        <div class="footer">
          <div class="copyright">
            © 2025-2026 Codans Studio. All rights reserved.
          </div>
        </div>
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<style>
#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .content {
    margin: 0 !important;
    margin-bottom: 60px !important; /* 为底部导航栏留出空间 */
  }
  
  .content-wrapper {
    padding: 12px !important;
    min-height: calc(100vh - 120px) !important;
    border-radius: 0 !important;
  }
  
  .header {
    padding: 0 12px !important;
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 1000;
  }
  
  .mobile-header {
    flex: 1;
    display: flex;
    align-items: center;
  }
  
  .app-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
  
  /* 移动端底部导航栏样式 */
  .mobile-tab-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: #fff;
    display: flex;
    border-top: 1px solid #f0f0f0;
    z-index: 1000;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  }
  
  .tab-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 6px 0;
    color: #666;
    font-size: 12px;
  }
  
  .tab-item.active {
    color: #1890ff;
  }
  
  .tab-icon {
    font-size: 20px;
    margin-bottom: 4px;
  }
  
  .tab-label {
    font-size: 12px;
  }
  
  /* 调整内容区域，避免被底部导航栏遮挡 */
  .ant-layout-content {
    padding-top: 64px !important; /* 头部高度 */
    padding-bottom: 60px !important; /* 底部导航栏高度 */
  }
}

/* 侧边栏样式 */
.logo {
  height: 32px;
  margin: 16px;
  background: rgba(255, 255, 255, 0.2);
}

/* 头部样式 */
.header {
  background: #fff !important;
  padding: 0 24px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  position: relative;
  z-index: 9;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 12px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

/* 内容区域 */
.content {
  margin: 16px;
  transition: all 0.2s;
}

.content-wrapper {
  background: #fff;
  min-height: calc(100vh - 180px);
  padding: 24px;
  border-radius: 4px;
}

.footer {
  text-align: center;
  padding: 16px 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  margin-top: 24px;
}

.copyright {
  margin: 0 auto;
}

/* 用户信息 */
.user-info {
  margin-right: 8px;
}

.username {
  margin-left: 8px;
}

/* 移动端优化 */
.ant-drawer-body {
  padding: 0;
}

.ant-layout-sider {
  box-shadow: 2px 0 8px 0 rgba(29, 35, 41, 0.05);
}

.logo {
  display:flex;
  align-items: center;
  justify-content: center;
  background: none;
}

.logo img {
  height: 32px;
}

</style>
